import { Empty } from "antd";
import { memo, useMemo } from "react";
import type { SourceDashboard, SourceType } from "../types";

const sourceMeta: Record<SourceType, { label: string; color: string }> = {
  PLAYER_REFERRAL: { label: "玩家推荐", color: "#1677ff" },
  BILIBILI: { label: "B站外招", color: "#36cfc9" },
  DOUYIN: { label: "抖音外招", color: "#722ed1" },
  LEGACY: { label: "历史来源", color: "#faad14" }
};

export const SourceDonut = memo(function SourceDonut({
  rows
}: {
  rows: SourceDashboard["sourceCounts"];
}) {
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  const gradient = useMemo(() => {
    let cursor = 0;
    const stops = rows.map((row) => {
      const start = cursor;
      cursor += total ? row.count / total * 100 : 0;
      return `${sourceMeta[row.source].color} ${start}% ${cursor}%`;
    });
    return `conic-gradient(${stops.join(", ")})`;
  }, [rows, total]);

  if (!total) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无真实来源数据" />;
  }

  return (
    <div className="source-donut-wrap">
      <div className="source-donut" style={{ background: gradient }}>
        <div className="source-donut__core">
          <strong>{total.toLocaleString()}</strong>
          <span>总申请</span>
        </div>
      </div>
      <div className="source-legend">
        {rows.map((row) => (
          <div className="source-legend__row" key={row.source}>
            <i style={{ background: sourceMeta[row.source].color }} />
            <span>{sourceMeta[row.source].label}</span>
            <b>{row.count}</b>
            <em>{total ? `${(row.count / total * 100).toFixed(1)}%` : "0%"}</em>
          </div>
        ))}
      </div>
    </div>
  );
});

export const Funnel = memo(function Funnel({
  rows
}: {
  rows: SourceDashboard["funnel"];
}) {
  if (!rows.length || rows.every((row) => row.value === 0)) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无真实转化数据" />;
  }
  const max = Math.max(1, rows[0]?.value ?? 1);
  return (
    <div className="funnel">
      {rows.map((row, index) => {
        const width = Math.max(34, row.value / max * 100);
        const previous = index ? rows[index - 1]?.value ?? row.value : row.value;
        const retention = previous ? row.value / previous * 100 : 0;
        return (
          <div className="funnel__stage" key={row.key}>
            <div className="funnel__meta">
              <span>{String(index + 1).padStart(2, "0")} · {row.label}</span>
              <b>{row.value.toLocaleString()}</b>
            </div>
            <div className="funnel__track">
              <div style={{ width: `${width}%` }} />
            </div>
            <small>{index === 0 ? "入口基准" : `环节留存 ${retention.toFixed(1)}%`}</small>
          </div>
        );
      })}
    </div>
  );
});

export const TrendChart = memo(function TrendChart({
  rows,
  emptyDescription = "当前周期暂无真实变化"
}: {
  rows: SourceDashboard["trend"];
  emptyDescription?: string;
}) {
  if (!rows.length || rows.every((row) => row.total === 0 && row.qualified === 0)) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyDescription} />;
  }
  const width = 720;
  const height = 220;
  const padding = 24;
  const max = Math.max(1, ...rows.flatMap((row) => [row.total, row.qualified]));
  const points = (key: "total" | "qualified") => rows.map((row, index) => {
    const x = padding + (rows.length <= 1 ? 0 : index / (rows.length - 1) * (width - padding * 2));
    const y = height - padding - row[key] / max * (height - padding * 2);
    return `${x},${y}`;
  }).join(" ");

  return (
    <div className="trend">
      <div className="trend__legend">
        <span><i className="trend__dot trend__dot--total" />新增申请</span>
        <span><i className="trend__dot trend__dot--qualified" />当日生效邀请</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="近期来源趋势">
        <defs>
          <linearGradient id="trend-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#1677ff" stopOpacity=".2" />
            <stop offset="1" stopColor="#1677ff" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((ratio) => (
          <line
            key={ratio}
            x1={padding}
            y1={height * ratio}
            x2={width - padding}
            y2={height * ratio}
            stroke="#f0f0f0"
          />
        ))}
        {rows.length > 1 && (
          <polygon
            points={`${padding},${height - padding} ${points("total")} ${width - padding},${height - padding}`}
            fill="url(#trend-fill)"
          />
        )}
        <polyline points={points("total")} fill="none" stroke="#1677ff" strokeWidth="4" />
        <polyline points={points("qualified")} fill="none" stroke="#36cfc9" strokeWidth="3" />
      </svg>
      <div className="trend__axis">
        {rows.map((row) => <span key={row.date}>{row.date.slice(5)}</span>)}
      </div>
    </div>
  );
});

export { sourceMeta };
